import axios from 'axios'
import {useWuliu178AuthStore} from '@store/auth.js'

// 根据当前地址自动判断运行环境
const isDev = () => {
    // 如果当前页面协议是 file: 或者地址中包含 127.0.0.1/localhost，则认为是开发环境
    return (
        location.protocol === 'file:' ||
        ['127.0.0.1', 'localhost'].includes(location.hostname)
    )
}

export const getBaseURL = () => {
    if (isDev()) {
        return 'http://127.0.0.1:8000/api/v1'       // 开发环境
    }
    // return 'https://api.wuliu178.com/api/v1' // 生产环境
    // 从 hostname 中提取主域名（去掉 www. 前缀）
    const hostname = location.hostname.replace(/^www\./, '')
    return `https://api.${hostname}/api/v1`
}



// 创建全局唯一的 axios 实例
const axiosInstance = axios.create({
    baseURL: getBaseURL(),
    timeout: 30000,
    headers: {
        'Content-Type': 'application/json'
    }
})

// 添加请求拦截器（只设置一次）
axiosInstance.interceptors.request.use(
    async (config) => {
        const wuliu178Auth = useWuliu178AuthStore()
        let jwt = wuliu178Auth.access_token

        // 如果存在 token，先判断是否过期
        if (jwt && wuliu178Auth.isExpired) {
            try {
                // 使用 refresh_token 刷新 access_token
                const res = await axios.post(`${getBaseURL()}/token/refresh`, {
                    refresh: wuliu178Auth.refresh_token
                })

                wuliu178Auth.access_token.value=res.data.access
                jwt =  res.data.access
            } catch (err) {
                // 刷新失败，清除 token 并跳转登录
                wuliu178Auth.logout()
                const currentUrl = window.location.href
                sessionStorage.setItem('redirectAfterLogin', currentUrl)
                window.location.replace('/')
                return Promise.reject(err)
            }
        }

        // 动态添加 token 到请求头
        if (jwt) {
            config.headers.Authorization = `Bearer ${jwt}`
        }

        return config
    },
    (error) => {
        return Promise.reject(error)
    }
)

// 添加响应拦截器（统一错误处理)
axiosInstance.interceptors.response.use(
    (response) => {
        // 对于blob类型响应，返回完整响应对象以保留headers
        if (response.config.responseType === 'blob') {
            return response
        }

        // 获取响应数据
        const responseData = response.data

        // 检查响应数据中的code字段是否为401
        if (responseData && responseData.code === 401) {
            console.error('未授权，请重新登录')
            // 暂存当前完整 URL（含 query）
            const currentUrl = window.location.href
            sessionStorage.setItem('redirectAfterLogin', currentUrl)

            // 跳转到首页并提示登录
            window.location.replace('/login')

            // 登录完成后由首页逻辑读取 redirectAfterLogin 并跳转
            return Promise.reject(new Error('未授权，请重新登录'))
        }

        // 直接返回数据部分，简化使用
        return responseData
    },
    (error) => {
        console.error('API请求失败:', error)

        // 统一错误处理逻辑
        if (error.response) {
            // 服务器返回了错误状态码
            const status = error.response.code
            switch (status) {
                case 401:
                    console.error('未授权，请重新登录')
                    // 暂存当前完整 URL（含 query）
                    const currentUrl = window.location.href
                    sessionStorage.setItem('redirectAfterLogin', currentUrl)

                    // 跳转到首页并提示登录
                    window.location.replace('/login')

                    // 登录完成后由首页逻辑读取 redirectAfterLogin 并跳转
                    break
                case 403:
                    console.error('权限不足')
                    break
                case 404:
                    console.error('请求资源不存在')
                    break
                case 500:
                    console.error('服务器内部错误')
                    break
                default:
                    console.error(`请求错误: ${status}`)
            }
        } else if (error.request) {
            // 请求已发出但没有收到响应
            console.error('网络错误，请检查网络连接')
        } else {
            // 其他错误
            console.error('请求配置错误:', error.message)
        }

        return Promise.reject(error)
    }
)

// 主请求函数（现在使用全局实例）
export const requestWuliu178Api = async (url, data = {}, method = 'post', config = {}) => {
    const requestConfig = {
        url,
        method,
        [method.toLowerCase() === 'get' ? 'params' : 'data']: data,
        ...config // 合并额外配置
    }

    return axiosInstance(requestConfig)
}

// HTTP方法快捷方式
export const getWlApi = (url, params = {}, config = {}) => requestWuliu178Api(url, params, 'get', config)
export const postWlApi = (url, data = {}, config = {}) => requestWuliu178Api(url, data, 'post', config)
export const putWlApi = (url, data = {}, config = {}) => requestWuliu178Api(url, data, 'put', config)
export const deleteWlApi = (url, data = {}, config = {}) => requestWuliu178Api(url, data, 'delete', config)
